import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import VideoFilters from "@/components/feature/VideoFilters";
import EmptyState from "@/components/base/EmptyState";
import SectionHeading from "@/components/base/SectionHeading";
import { getLatestVideos, searchVideos, sortVideos, type SortKey } from "@/lib/videos";
import { usePageMeta } from "@/hooks/usePageMeta";

const hotKeywords = ["综艺", "夜宵", "猫咪", "大理", "翻唱", "职场"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [sort, setSort] = useState<SortKey>("hottest");

  usePageMeta({
    title: keyword ? `${keyword} - 茄子视频` : "搜索短视频 - 茄子视频",
    description: keyword
      ? `茄子视频为你找到与“${keyword}”相关的短视频内容，支持按最新与最热排序，结果丰富多样。`
      : "在茄子视频搜索短视频，输入关键词即可找到娱乐、生活、搞笑、音乐、美食、旅行等分类的内容。",
    keywords: keyword
      ? `${keyword},${keyword}视频,茄子视频搜索,短视频`
      : "茄子视频搜索,短视频搜索,视频搜索",
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => sortVideos(searchVideos(keyword), sort), [keyword, sort]);
  const recommendations = useMemo(() => getLatestVideos(8), []);

  const runSearch = (value: string) => {
    const trimmed = value.trim();
    setParams(trimmed ? { q: trimmed } : {});
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    runSearch(input);
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1360px] px-4 pt-6 md:px-8 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

        <div className="mt-5 rounded-2xl bg-primary-50 px-5 py-7 md:px-8 md:py-9">
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
            Search the library
          </p>
          <h1 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-[32px]">
            搜索短视频
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            支持标题、简介、标签与作者的全文检索，例如“综艺”“大理”“猫咪”。
          </p>

          <form onSubmit={submitSearch} className="mt-5 flex flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 items-center rounded-full border border-background-300 bg-background-50 px-4 py-2.5 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入关键词，按回车搜索"
                className="w-full bg-transparent px-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => setInput("")}
                  aria-label="清空关键词"
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-foreground-400 hover:bg-background-100"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="font-label text-[11px] text-foreground-500">热门搜索</span>
            {hotKeywords.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => runSearch(item)}
                className="cursor-pointer whitespace-nowrap rounded-full bg-background-50/80 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:bg-background-50 hover:text-primary-700"
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto mt-8 w-full max-w-[1360px] px-4 pb-4 md:px-8">
        {keyword ? (
          <>
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <p className="text-sm text-foreground-500">搜索结果</p>
                <h2 className="mt-1 font-heading text-lg font-extrabold text-foreground-950 md:text-xl">
                  “{keyword}” 共找到{" "}
                  <span className="text-primary-600">{results.length}</span> 条视频
                </h2>
              </div>
              {results.length > 0 && (
                <Link
                  to="/"
                  className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
                >
                  返回首页
                </Link>
              )}
            </div>

            <div className="mt-4">
              <VideoFilters
                sort={sort}
                duration="all"
                onSortChange={setSort}
                onDurationChange={() => undefined}
                onReset={() => setSort("hottest")}
                resultCount={results.length}
                showDuration={false}
              />
            </div>

            {results.length > 0 ? (
              <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
                {results.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="mt-6">
                <EmptyState
                  icon="ri-search-eye-line"
                  title="没有找到相关视频"
                  description={`暂时没有与“${keyword}”匹配的内容，换一个关键词试试，或者看看下面这些热门推荐。`}
                >
                  <button
                    type="button"
                    onClick={() => runSearch("")}
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-refresh-line"></i>
                    重置搜索
                  </button>
                  <Link
                    to="/"
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    返回首页
                  </Link>
                </EmptyState>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-xl border border-dashed border-background-300 bg-background-100/60 px-5 py-10 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-500">
              <i className="ri-search-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              输入关键词，开始探索
            </h2>
            <p className="mt-1.5 text-sm text-foreground-500">
              搜索功能为前端演示，匹配范围包括视频标题、简介、标签与作者名。
            </p>
          </div>
        )}

        <section className="mt-14">
          <SectionHeading
            eyebrow="You may also like"
            title={keyword && results.length === 0 ? "换换口味：热门推荐" : "大家都在看"}
            description="这些是近期播放量较高的作品，和搜索关键词无关，但同样值得点开。"
            action={
              <Link
                to="/category/entertainment"
                className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
              >
                浏览分类
              </Link>
            }
          />
          <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
            {recommendations.slice(0, 4).map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}